import { ChangeDetectionStrategy, Component, computed, inject, viewChild, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';

import { firstValueFrom, map } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectUiState, selectUser } from '../../../../store/app.selectors';

import { getTagValue, resolveOwner } from '../../../../core/utils/owner-match';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { ImagesService } from '../../../shared/services/images.service';
import { uiInitialState } from '../../../shared/store/ui.reducer';
import { setShowChannel, setShowChannels } from '../../../shared/store/ui.actions';
import { ChannelType } from '../../interfaces/IChannel';
import { ContactDirectoryService } from '../../services/contact-directory/contact-directory-api.service';
import { MessagingApiService } from '../../services/messaging-api/messaging-api.service';
import { SmartRepliesService } from '../../services/smart-replies.service';
import { TranslaterService } from '../../services/translater.service';
import {
  changeBotMode,
  deselectChannel,
  sendMessage,
  setChannelTranslationSetting,
  setSmartRepliesStatus,
  setVideoCall,
  toggleSidebar,
} from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';
import { LanguageSelectorComponent } from '../languaje-selector/language-selector.component';
import { ListMembersChannelToCallModalComponent } from '../list-members-channel-to-call/list-members-channel-to-call.component';
import { PhoneEditFormComponent } from '../phone-edit-form/phone-edit-form.component';

@Component({
  selector: 'ngx-channel-header',
  templateUrl: './channel-header.component.html',
  styleUrls: ['./channel-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChannelHeaderComponent extends BaseComponent {
  private store = inject(Store<AppStateWithChat>);
  public dialog = inject(MatDialog);
  private messagingApiService = inject(MessagingApiService);
  public translaterService = inject(TranslaterService);
  private contactDirectoryService = inject(ContactDirectoryService);
  public imagesService = inject(ImagesService);
  public smartRepliesService = inject(SmartRepliesService);

  ui = toSignal(this.store.select(selectUiState), { initialValue: uiInitialState });
  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });
  user = toSignal(this.store.select(selectUser), { initialValue: null });

  selectedChannel = computed(() => this.chat().selectedChannel);

  channelServiceIcon(): string {
    return this.selectedChannel()?.routing?.toLowerCase() ?? '';
  }
  showBackArrow = computed(() => this.ui().showBackArrow);
  hideInfoButton = computed(() => this.ui().hideInfoButton);
  hideInputChat = computed(() => this.ui().hideInputChat);
  hideEditPhone = computed(() => this.ui().hideEditPhone);
  videoCall = computed(() => this.chat().videoCall);

  ownerName(): string {
    const channel = this.selectedChannel();
    const ownerId = getTagValue(channel?.systemTags, 'ownerId') || channel?.owner?.id;
    return (
      resolveOwner(ownerId, channel?.owner, {
        users: this.chat().users,
        tagDisplayName: getTagValue(channel?.systemTags, 'ownerDisplayName'),
      })?.displayName || ''
    );
  }

  awayOwnerName(): string {
    const channel = this.selectedChannel();
    const ownerId = getTagValue(channel?.systemTags, 'originalOwnerId') || channel?.awayOwner?.id;
    return (
      resolveOwner(ownerId, channel?.awayOwner, {
        users: this.chat().users,
        tagDisplayName: getTagValue(channel?.systemTags, 'originalOwnerDisplayName'),
      })?.displayName || ''
    );
  }

  languageMenuTrigger = viewChild<MatMenuTrigger>('languageMenuTrigger');
  ownerMenuTrigger = viewChild<MatMenuTrigger>('ownerMenuTrigger');
  languageSelectorComponent = viewChild(LanguageSelectorComponent);

  toggleSidebar(active: boolean) {
    this.store.dispatch(toggleSidebar({ active }));
  }

  onCloseLanguageMenu() {
    this.languageSelectorComponent()?.resetFilter();
    this.languageMenuTrigger()?.closeMenu();
  }

  onLanguageMenuClosed() {
    this.languageSelectorComponent()?.resetFilter();
  }

  onCloseOwnerMenu() {
    this.ownerMenuTrigger()?.closeMenu();
  }

  goBack() {
    this.store.dispatch(deselectChannel());
    this.store.dispatch(setShowChannels({ value: true }));
    this.store.dispatch(setShowChannel({ value: false }));
  }

  async startVideoCall() {
    const selectedChannel = this.selectedChannel();
    const videoCall = this.videoCall();
    if (!videoCall && selectedChannel) {
      const roomName = selectedChannel.uid;
      if (selectedChannel.type === ChannelType.DIRECT_MESSAGE) {
        this.setVideoCall(roomName);
        this.notifyMembers(selectedChannel.members?.map((member) => member.businessReference!.entityUID) ?? [], roomName);
      } else {
        const members = await this.getMembersOfChannel();
        if (members) {
          this.setVideoCall(roomName);
          this.notifyMembers(members, roomName);
        }
      }
    }
  }

  setVideoCall(roomName: string): void {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return;
    }
    this.store.dispatch(
      setVideoCall({
        videoCall: {
          room: roomName,
          name: selectedChannel.displayName,
          type: 0,
          pic: selectedChannel.imageURL,
        },
      })
    );
  }

  getMembersOfChannel(): Promise<string[] | undefined> {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return Promise.resolve(undefined);
    }
    if ((selectedChannel.totalMembersCount ?? 0) <= 2) {
      return firstValueFrom(
        this.messagingApiService
          .getMembersChannel(0, 100)
          .pipe(map((result) => result.map((member) => member.businessReference!.entityUID)))
      );
    } else {
      return firstValueFrom(
        this.dialog
          .open(ListMembersChannelToCallModalComponent, {
            width: '300px',
          })
          .afterClosed()
      );
    }
  }

  notifyMembers(members: string[], room: string) {
    const user = this.user();
    if (members.length && user?.subjectMessageableEntity?.businessReference?.entityUID) {
      members = members.filter(
        (memberUID) => Number(memberUID) !== Number(user.subjectMessageableEntity!.businessReference!.entityUID)
      );
      this.contactDirectoryService.notifyVideoCallMembers(members, room).subscribe();
      this.store.dispatch(
        sendMessage({
          message: `vc=${room}`,
          attachments: [],
        })
      );
    }
  }

  setTranslaterSetting(): void {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return;
    }
    this.store.dispatch(
      setChannelTranslationSetting({
        channelUID: selectedChannel.uid,
        value: !selectedChannel.hideTranslateSetting,
      })
    );
  }

  setBotMode(): void {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return;
    }
    this.store.dispatch(changeBotMode({ channel: selectedChannel, value: selectedChannel.isBotMode ?? false }));
  }

  showSmartReplies(): void {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return;
    }
    this.store.dispatch(setSmartRepliesStatus({ value: false, channel: selectedChannel }));
  }

  editPhoneNumber(): void {
    const selectedChannel = this.selectedChannel();
    if (!selectedChannel) {
      return;
    }
    this.dialog.open(PhoneEditFormComponent, {
      width: '450px',
      data: { channel: selectedChannel },
    });
  }
}
